import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { Box, IconButton, Portal, TextField, Tooltip } from '@mui/material'
import { MRT_RowData } from 'material-react-table'
import { useCallback, useEffect, useRef, useState } from 'react'

import { getInitCellValue, MrtCellProps } from '@/components'
import { getRowElementLabel } from '@/components/EditorModalHeader'
import { useAppContext } from '@/hooks'

import { useSelectRowOnOpen } from '../hooks/useSelectRowOnOpen'
import { focusAdjacentTableCell } from '../utils'
import { CellTooltip } from './CellTooltip'
import { FullscreenEditorModal } from './FullscreenEditorModal'

const EXPANDED_EDITOR_HEIGHT = 100
const MIN_TOOLTIP_LENGTH = 13
const DISPLAY_VALUE_TRUNCATE_LENGTH = 50
const MIN_CELL_HEIGHT = 32
const FULLSCREEN_TOGGLE_DELAY_MS = 50

const useElementPosition = (ref: React.RefObject<HTMLElement>, isExpanded: boolean) => {
	const [position, setPosition] = useState<DOMRect | null>(null)

	useEffect(() => {
		if (!ref.current) {
			setPosition(null)
			return
		}

		const findTableCell = (el: HTMLElement | null): HTMLElement | null => {
			while (el) {
				if (el.tagName === 'TD' || el.tagName === 'TH') {
					return el
				}
				el = el.parentElement
			}
			return null
		}

		const tableCell = findTableCell(ref.current)
		const targetElement = tableCell || ref.current

		const updatePosition = () => {
			setPosition(targetElement.getBoundingClientRect())
		}

		updatePosition()
		
		if (isExpanded) {
			window.addEventListener('scroll', updatePosition, true)
			window.addEventListener('resize', updatePosition)

			return () => {
				window.removeEventListener('scroll', updatePosition, true)
				window.removeEventListener('resize', updatePosition)
			}
		}
	}, [ref, isExpanded])

	return position
}

/**
 * Expandable Text Cell for MRT tables with expandable inline editor
 * Same pattern as MonacoScriptCell but for plain text/markdown content
 *
 * Click on cell to expand TextField inline (rolls down within cell).
 * Fullscreen modal available via icon.
 *
 * Keyboard shortcuts:
 * - F11: Open fullscreen editor
 * - Ctrl+S / Cmd+S: Save and collapse
 * - Esc: Cancel and collapse
 */
export const ExpandableTextCell = <TData extends MRT_RowData>({
	cell,
	column,
	isDisabled = false,
	isReadOnly = false,
	row,
	updateCellValue,
	customValue,
}: MrtCellProps<TData>) => {
	const { t } = useAppContext()
	const cellValue = customValue !== undefined ? customValue : cell?.getValue()
	const [editorValue, setEditorValue] = useState<string>(
		getInitCellValue(customValue, cellValue),
	)
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
	const [isExpanded, setIsExpanded] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	const cellPosition = useElementPosition(containerRef, isExpanded)

	useEffect(() => {
		setEditorValue(getInitCellValue(customValue, cellValue))
	}, [cellValue, customValue])

	useEffect(() => {
		if (isExpanded) {
			// Small delay to ensure Portal and TextField are rendered
			const timer = setTimeout(() => {
				const textarea = textareaRef.current
				if (textarea) {
					textarea.focus()
					// Place cursor at end of text
					const length = textarea.value?.length || 0
					textarea.setSelectionRange(length, length)
				}
			}, 50)
			return () => clearTimeout(timer)
		}
	}, [isExpanded])

	const handleSave = useCallback(
		(focusAfter?: 'current' | 'next' | 'prev') => {
			const effectiveReadOnly = isDisabled || isReadOnly
			if (!effectiveReadOnly && row?.index !== undefined && column?.id) {
				updateCellValue?.(row.index, column.id as keyof TData, editorValue)
			}
			setIsExpanded(false)
			setTimeout(() => {
				if (focusAfter === 'next' || focusAfter === 'prev') {
					focusAdjacentTableCell(containerRef, focusAfter)
				} else {
					const focusableBox = containerRef.current?.querySelector('[tabindex="0"]') as HTMLElement
					focusableBox?.focus()
				}
			}, 50)
		},
		[isDisabled, isReadOnly, row?.index, column?.id, editorValue, updateCellValue],
	)

	const handleCancel = useCallback(() => {
		setEditorValue(getInitCellValue(customValue, cellValue))
		setIsExpanded(false)
		// Focus back on the cell for continued keyboard navigation
		setTimeout(() => {
			const focusableBox = containerRef.current?.querySelector('[tabindex="0"]') as HTMLElement
			focusableBox?.focus()
		}, 50)
	}, [customValue, cellValue])

	const handleFullscreenSave = useCallback(
		(newValue: string) => {
			setEditorValue(newValue)
			if (row?.index !== undefined && column?.id) {
				updateCellValue?.(row.index, column.id as keyof TData, newValue)
			}
			setIsFullscreenOpen(false)
			setIsExpanded(false)
			// Focus back on the cell for continued keyboard navigation
			setTimeout(() => {
				const focusableBox = containerRef.current?.querySelector('[tabindex="0"]') as HTMLElement
				focusableBox?.focus()
			}, 50)
		},
		[row?.index, column?.id, updateCellValue],
	)

	const handleFullscreenClose = useCallback(() => {
		setIsFullscreenOpen(false)
		// Focus back on the cell for continued keyboard navigation
		setTimeout(() => {
			const focusableBox = containerRef.current?.querySelector('[tabindex="0"]') as HTMLElement
			focusableBox?.focus()
		}, 50)
	}, [])

	useEffect(() => {
		if (!isExpanded && !isFullscreenOpen) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault()
				e.stopPropagation()
				if (isFullscreenOpen) {
					setIsFullscreenOpen(false)
				} else {
					handleCancel()
				}
			}
			if ((e.ctrlKey || e.metaKey) && e.key === 's') {
				e.preventDefault()
				handleSave()
			}
			if (e.key === 'F11') {
				e.preventDefault()
				setIsFullscreenOpen(true)
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [isExpanded, isFullscreenOpen, handleSave, handleCancel])

	useSelectRowOnOpen(isExpanded || isFullscreenOpen, row?.original)

	const effectiveReadOnly = isDisabled || isReadOnly
	const columnName = column?.columnDef.header ?? ''
	const elementLabel = getRowElementLabel(row?.original)
	const hasContent = editorValue && editorValue.trim().length > 0

	const showTooltip = (editorValue?.length || 0) > MIN_TOOLTIP_LENGTH
	const displayValue = editorValue
		? editorValue.length > DISPLAY_VALUE_TRUNCATE_LENGTH
			? editorValue.substring(0, DISPLAY_VALUE_TRUNCATE_LENGTH).replace(/\n/g, ' ') + '...'
			: editorValue.replace(/\n/g, ' ')
		: ''

	return (
		<>
			<Box
				ref={containerRef}
				sx={{
					position: 'relative',
					width: '100%',
					minHeight: MIN_CELL_HEIGHT,
				}}
			>
				{!isExpanded && (
					<CellTooltip
						title={showTooltip ? editorValue : undefined}
						isMenuOpen={isFullscreenOpen}
						isEditMode={!effectiveReadOnly}
					>
						<Box
							tabIndex={effectiveReadOnly ? -1 : 0}
							sx={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								minHeight: MIN_CELL_HEIGHT,
								gap: 0.5,
								cursor: effectiveReadOnly ? 'default' : 'text',
								'&:focus': {
									outline: 'none',
								},
							}}
							onClick={() => !effectiveReadOnly && setIsExpanded(true)}
							onKeyDown={(e) => {
								if (!effectiveReadOnly && e.key === 'Enter') {
									e.preventDefault()
									e.stopPropagation()
									setIsExpanded(true)
								}
							}}
						>
							<Box
								component="span"
								sx={{
									flex: 1,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									color: hasContent ? 'inherit' : 'text.disabled',
								}}
							>
								{displayValue || '—'}
							</Box>

							<Box
								sx={{
									display: 'flex',
									opacity: 0,
									transition: 'opacity 0.15s',
									'.MuiTableCell-root:hover &, .MuiTableCell-root:focus-within &':
										{
											opacity: 1,
										},
								}}
							>
								<Tooltip title={t('TOGGLE_FULLSCREEN')}>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation()
											setIsFullscreenOpen(true)
										}}
										sx={{ p: 0.25 }}
									>
										<FullscreenIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
					</CellTooltip>
				)}

			</Box>

			{isExpanded && cellPosition && (
				<Portal>
					<Box
						onClick={handleSave}
						sx={{
							position: 'fixed',
							inset: 0,
							zIndex: 1200,
							bgcolor: 'transparent',
						}}
					/>
					<Box
						onClick={(e) => e.stopPropagation()}
						sx={{
							position: 'fixed',
							top: cellPosition.top,
							right: window.innerWidth - cellPosition.right,
							width: cellPosition.width,
							zIndex: 1201,
							overflow: 'hidden',
						}}
					>
					<Box
						sx={{
							borderLeft: '1px solid',
							borderRight: '1px solid',
							borderBottom: '1px solid',
							borderColor: 'primary.main',
							borderBottomLeftRadius: 4,
							borderBottomRightRadius: 4,
							overflow: 'hidden',
							bgcolor: 'background.paper',
							boxShadow: 2,
						}}
					>
							<TextField
								inputRef={textareaRef}
								multiline
								fullWidth
								value={editorValue}
								onChange={(e) => setEditorValue(e.target.value)}
								disabled={effectiveReadOnly}
								variant="outlined"
								onKeyDown={(e) => {
									// Stop propagation for arrow keys to prevent cell navigation
									if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
										e.stopPropagation()
									}
									// Tab: Save, collapse, move to next cell
									if (e.key === 'Tab') {
										e.preventDefault()
										e.stopPropagation()
										handleSave(e.shiftKey ? 'prev' : 'next')
										return
									}
									// Ctrl/Cmd + S: Save and close
									if ((e.ctrlKey || e.metaKey) && e.key === 's') {
										e.preventDefault()
										e.stopPropagation()
										handleSave()
									}
									// Escape: Cancel and close
									if (e.key === 'Escape') {
										e.preventDefault()
										e.stopPropagation()
										handleCancel()
									}
								}}
								sx={{
									'& .MuiOutlinedInput-root': {
										'& fieldset': { border: 'none' },
										borderRadius: 0,
										padding: 0,
									},
									'& .MuiInputBase-input': {
										fontSize: '0.875rem',
										fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
										minHeight: EXPANDED_EDITOR_HEIGHT,
										padding: '4px 8px',
									},
								}}
							/>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									alignItems: 'center',
									px: 0.75,
									py: 0.25,
									bgcolor: 'grey.100',
									borderTop: '1px solid',
									borderColor: 'divider',
								}}
							>
								<Tooltip title={t('TOGGLE_FULLSCREEN')}>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation()
											setIsFullscreenOpen(true)
											setTimeout(() => setIsExpanded(false), FULLSCREEN_TOGGLE_DELAY_MS)
										}}
										sx={{ p: 0.25 }}
									>
										<FullscreenIcon sx={{ fontSize: 14 }} />
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
					</Box>
				</Portal>
			)}

			<FullscreenEditorModal
				open={isFullscreenOpen}
				onClose={handleFullscreenClose}
				onSave={handleFullscreenSave}
				value={editorValue}
				title={String(columnName)}
				elementLabel={elementLabel}
				language="text"
				readOnly={effectiveReadOnly}
			/>
		</>
	)
}
